// Conditional Sentry import - only loads if DSN is available
if (import.meta.env.VITE_SENTRY_DSN) {
  import('../sentry.client.config');
}
import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import App from './App';
import './index.css';
import { unregisterServiceWorker, setupInstallPrompt } from './utils/pwa';

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find the root element');

// Initialize PWA features
if (import.meta.env.PROD) {
  unregisterServiceWorker();
  setupInstallPrompt();
}

const app = (
  <StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </StrictMode>
);

// Page chunks for the prerendered SEO routes (same dynamic imports as
// App.tsx, so Vite resolves them to the same chunks). Preloading the current
// route's chunk before hydrateRoot lets React hydrate the lazy Suspense
// boundary immediately instead of leaving it dehydrated during the network
// fetch — avoiding a client-side re-render of the prerendered content.
const prerenderedPagePreloaders: Array<[RegExp, () => Promise<unknown>]> = [
  [/^\/$/, () => import('./pages/Home')],
  [/^\/enneagram$/, () => import('./pages/EnneagramIndex')],
  [/^\/enneagram\/type\/\d+$/, () => import('./pages/TypePage')],
  [/^\/enneagram\/type\/\d+\/wing\/\d+$/, () => import('./pages/WingPage')],
  [/^\/contact$/, () => import('./pages/Contact')],
  [/^\/terms$/, () => import('./pages/TermsOfService')],
  [/^\/become-expert$/, () => import('./pages/BecomeExpert')],
  [/^\/for-contributors$/, () => import('./pages/ForContributors')],
];

async function start() {
  // Prerendered pages ship with server HTML in #root — hydrate it so the
  // content stays visible while React attaches. Non-prerendered SPA routes
  // get the empty shell and a normal client render.
  if (rootElement.hasChildNodes()) {
    const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
    const preloader = prerenderedPagePreloaders.find(([re]) => re.test(currentPath));
    if (preloader) {
      try {
        await preloader[1]();
      } catch {
        // Chunk fetch failure — hydrate anyway; Suspense falls back to
        // client rendering as before.
      }
    }
    hydrateRoot(rootElement, app);
  } else {
    createRoot(rootElement).render(app);
  }
}

void start();